import type { FaceDesign, RenderObject, TablePiece, TableZone, Vec3 } from '@v2/contracts'
import { actionTargets, type Projection } from '@v2/sdk'
import { heldZone } from './hand'

const DECORATION = '__table:'
const HEADER = 0.48
const PADDING = 0.22
const GAP = 0.2
const MAT_TOP = 0.075
// A side mat needs to clear the full central workspace plus the inter-zone gap.
const SIDE_X = (10.25 + 2.4) / 2 + GAP + 0.1

interface ZonePlan {
  readonly zone: TableZone
  readonly pieces: readonly TablePiece[]
  readonly automaticPieces: readonly TablePiece[]
  readonly columns: number
  readonly rows: number
  readonly cell: readonly [number, number]
  readonly width: number
  readonly depth: number
  readonly stacked: boolean
  readonly direction: number
  center: Vec3
}

/**
 * Compile only a viewer's public projection. Coordinates and physical materials
 * belong here; action legality, ownership and artwork visibility belong in rules.
 * Rows wrap at a readable component size and the table grows with its workspaces.
 */
export function compileScene(projection: Projection): readonly RenderObject[] {
  validateScene(projection)
  const highlighted = new Set(projection.actions.flatMap(actionTargets))
  const held = heldZone(projection)?.id
  const plans = projection.scene.zones.map((zone, index) => planZone(zone, index, projection))
  placeZones(plans.filter(plan => !(held === plan.zone.id)))

  const objects: RenderObject[] = []
  for (const plan of plans) {
    const inHand = (held === plan.zone.id)
    if (!inHand) objects.push(...createWorkspace(plan, Boolean(projection.viewer) && highlighted.has(plan.zone.id)))
    plan.pieces.forEach((piece) => {
      if (inHand && piece.kind !== 'card') return
      const position = piecePosition(plan, piece)
      const size = pieceSize(piece, plan.zone.role)
      const face = visibleFace(piece)
      // A projected deck is one anonymous proxy, never one mesh per secret card.
      const layers = !inHand && piece.kind === 'card' && (plan.stacked || plan.zone.role === 'deck' || piece.count !== undefined)
        ? Math.min(5, Math.max(1, Math.ceil(Math.log2(Math.max(1, piece.count ?? 1))) + 1))
        : 1
      for (let layer = 0; layer < layers - 1; layer += 1) {
        objects.push({
          id: `${DECORATION}stack:${piece.id}:${layer}`,
          shape: 'card',
          position: [position[0], position[1] + layer * 0.025, position[2]],
          rotation: piece.rotation ?? 0,
          size,
          color: '#dbd1b7',
          face: piece.back ?? (piece.hidden ? face : undefined),
          back: piece.back ?? (piece.hidden ? face : undefined),
          interactive: false,
          roughness: 0.9,
        })
      }
      objects.push({
        id: piece.id,
        shape: piece.kind === 'token' ? 'disc' : piece.kind === 'cube' || piece.kind === 'region' ? 'slab' : piece.kind,
        position: [position[0], position[1] + (layers - 1) * 0.025, position[2]],
        rotation: piece.rotation ?? 0,
        size,
        color: piece.hidden ? face.color : (piece.color ?? face.color),
        face: (piece.kind === 'cube' || piece.kind === 'train') && !piece.hidden ? piece.face : face,
        back: (piece.kind === 'cube' || piece.kind === 'train') && !piece.hidden ? piece.back : piece.hidden ? face : (piece.back ?? DEFAULT_BACK),
        // A public pile lights up when a choice can use one of its cards; the browser picks the card.
        interactive: Boolean(projection.viewer) && (highlighted.has(piece.id) || (!piece.hidden && Boolean(piece.contents?.some(item => highlighted.has(item.id))))),
        ...(piece.kind === 'region' ? { surface: 'region' as const } : {}),
        inspectable: true,
        highlighted: false,
        ...(inHand ? { layer: 'foreground' as const } : {}),
        roughness: piece.kind === 'card' ? 0.82 : piece.kind === 'token' ? 0.48 : piece.kind === 'board' ? 0.94 : 0.6,
      })
    })
  }
  return [...createTable(objects.filter(object => object.layer !== 'foreground')), ...objects]
}

const DEFAULT_BACK: FaceDesign = {
  title: 'Unrevealed', color: '#345b51', background: '#e8dfc8', style: 'ornament',
}

function visibleFace(piece: TablePiece): FaceDesign {
  // In particular, never use the face, label, description or color of a hidden
  // component as a fallback. This remains safe even for a malformed projection.
  if (piece.hidden) return piece.back ?? DEFAULT_BACK
  return piece.face ?? {
    title: piece.label,
    body: piece.description,
    value: piece.count,
    color: piece.color ?? '#476459',
    background: '#ece4cf',
    style: 'plain',
  }
}

function pieceSize(piece: TablePiece, role: TableZone['role']): Vec3 {
  if (piece.size) return piece.size
  if (piece.kind === 'region') return [1, 0.02, 0.4]
  if (piece.kind === 'train') return [1, 0.4, 0.4]
  if (piece.kind === 'board') return [8, 0.12, 6]
  if (piece.kind === 'token') return [0.92, 0.15, 0.92]
  if (piece.kind === 'pawn') return [0.58, 0.95, 0.58]
  if (piece.kind === 'cube') return [0.3, 0.3, 0.3]
  if (role === 'tableau') return [1.2, 0.055, 1.7]
  if (role === 'hand') return [1.45, 0.055, 2.05]
  return [1.6, 0.055, 2.25]
}

function planZone(zone: TableZone, index: number, projection: Projection): ZonePlan {
  const pieces = projection.scene.pieces.filter((piece) => piece.zone === zone.id)
  const automaticPieces = pieces.filter((piece) => piece.position === undefined)
  const playerIndex = projection.players.findIndex((player) => player.id === zone.owner)
  const direction = playerIndex >= 0 && playerIndex % 2 === 1 ? -1 : 1
  const ownerZ = playerIndex < 0 ? 3.5 : direction * (3.5 + Math.floor(playerIndex / 2) * 3.4)
  const isSide = zone.role === 'deck' || zone.role === 'hand' || zone.role === 'supply'
  const stacked = zone.layout === 'stack' || (zone.role === 'deck' && zone.layout === undefined)
  const columnLimit = zone.columns ?? (isSide ? (zone.role === 'supply' ? 2 : 1) : zone.role === 'tableau' ? 6 : 5)
  const columns = stacked ? 1 : Math.min(columnLimit, Math.max(1, automaticPieces.length))
  const rows = stacked ? 1 : Math.max(1, Math.ceil(automaticPieces.length / columns))
  const fallbackSize: Vec3 = zone.role === 'tableau' ? [1.2, 0.055, 1.7] : [1.6, 0.055, 2.25]
  const sizes = automaticPieces.length ? automaticPieces.map((piece) => footprint(pieceSize(piece, zone.role), piece.rotation ?? 0)) : [fallbackSize]
  const cell: readonly [number, number] = [
    Math.max(...sizes.map((size) => size[0])),
    Math.max(...sizes.map((size) => size[2])),
  ]
  const width = zone.size?.[0] ?? Math.max(isSide ? 2.4 : 10.25, columns * cell[0] + (columns - 1) * GAP + PADDING * 2)
  const depth = zone.size?.[1] ?? (rows * cell[1] + (rows - 1) * GAP + HEADER + PADDING * 2)
  const defaults: Record<TableZone['role'], Vec3> = {
    market: [0, 0, 0],
    tableau: [0, 0, ownerZ],
    deck: [-SIDE_X, 0, 0],
    hand: [SIDE_X, 0, ownerZ],
    supply: [-SIDE_X, 0, ownerZ],
    display: [0, 0, 7 + index * 0.1],
  }
  return { zone, pieces, automaticPieces, columns, rows, cell, width, depth, stacked, direction, center: zone.position ?? defaults[zone.role] }
}

function intersects(a: ZonePlan, b: ZonePlan): boolean {
  return Math.abs(a.center[0] - b.center[0]) < (a.width + b.width) / 2 + GAP
    && Math.abs(a.center[2] - b.center[2]) < (a.depth + b.depth) / 2 + GAP
}

/** Preserve authored positions; pack automatic workspaces away from the center. */
function placeZones(plans: readonly ZonePlan[]): void {
  const placed = plans.filter((plan) => plan.zone.position !== undefined)
  const priority: Record<TableZone['role'], number> = { market: 0, deck: 1, tableau: 2, hand: 3, supply: 4, display: 5 }
  const automatic = plans.filter((plan) => plan.zone.position === undefined)
    .sort((a, b) => priority[a.zone.role] - priority[b.zone.role])
  for (const plan of automatic) {
    // Every collision moves monotonically beyond an existing rectangle, so this
    // terminates even for many players, repeated roles or densely filled zones.
    let collision = placed.find((other) => intersects(plan, other))
    while (collision) {
      plan.center = [plan.center[0], plan.center[1], collision.center[2]
        + plan.direction * ((plan.depth + collision.depth) / 2 + GAP + 0.001)]
      collision = placed.find((other) => intersects(plan, other))
    }
    placed.push(plan)
  }
}

/** Authored components may overhang mats. Frame their rotated physical bounds too. */
function createTable(objects: readonly RenderObject[]): RenderObject[] {
  const bounds = objects.map((object) => ({ position: object.position, size: footprint(object.size, object.rotation) }))
  const minX = Math.min(-8.5, ...bounds.map((bound) => bound.position[0] - bound.size[0] / 2 - 0.6))
  const maxX = Math.max(8.5, ...bounds.map((bound) => bound.position[0] + bound.size[0] / 2 + 0.6))
  const minZ = Math.min(-6, ...bounds.map((bound) => bound.position[2] - bound.size[2] / 2 - 0.6))
  const maxZ = Math.max(6, ...bounds.map((bound) => bound.position[2] + bound.size[2] / 2 + 0.6))
  const x = (minX + maxX) / 2
  const z = (minZ + maxZ) / 2
  const width = maxX - minX
  const depth = maxZ - minZ
  return [
    { id: `${DECORATION}wood`, shape: 'slab', position: [x, -0.2, z], rotation: 0, size: [width, 0.36, depth], color: '#30281f', roughness: 0.58 },
    { id: `${DECORATION}inlay`, shape: 'slab', position: [x, -0.019, z], rotation: 0, size: [width - 0.28, 0.018, depth - 0.28], color: '#a88d52', roughness: 0.38 },
    { id: `${DECORATION}felt`, shape: 'slab', position: [x, 0, z], rotation: 0, size: [width - 0.36, 0.036, depth - 0.36], color: '#163e35', roughness: 1 },
  ]
}

function createWorkspace(plan: ZonePlan, highlighted: boolean): RenderObject[] {
  const [x, y, z] = plan.center
  const color = plan.zone.color ?? '#294e42'
  const padding = plan.zone.size ? Math.min(PADDING, plan.width * 0.1, plan.depth * 0.1) : PADDING
  const header = plan.zone.size ? Math.min(HEADER, plan.depth * 0.4) : HEADER
  return [
    {
      id: plan.zone.id, shape: 'slab', position: [x, y + 0.048, z], rotation: 0,
      size: [plan.width, 0.054, plan.depth], color, interactive: highlighted, highlighted: false, roughness: 0.94,
    },
    {
      id: `${DECORATION}label:${plan.zone.id}`, shape: 'slab',
      position: [x, y + MAT_TOP + 0.008, z - plan.depth / 2 + padding + header / 2],
      rotation: 0, size: [plan.width - padding * 2, 0.016, header], color,
      face: { title: plan.zone.label, color: '#d5c298', background: color, style: 'plain' },
      interactive: false, roughness: 0.94,
    },
  ]
}

function piecePosition(plan: ZonePlan, piece: TablePiece): Vec3 {
  const [x, y, z] = plan.center
  const size = pieceSize(piece, plan.zone.role)
  const surface = y + MAT_TOP + size[1] / 2 + 0.008
  if (piece.position) return [x + piece.position[0], surface + piece.position[1], z + piece.position[2]]
  const index = plan.automaticPieces.indexOf(piece)
  const row = plan.stacked ? 0 : Math.floor(index / plan.columns)
  const column = plan.stacked ? 0 : index % plan.columns
  const rowLength = plan.stacked ? 1 : Math.min(plan.columns, plan.automaticPieces.length - row * plan.columns)
  return [
    x + (column - (rowLength - 1) / 2) * (plan.cell[0] + GAP),
    surface + (plan.stacked ? index * 0.065 : 0),
    z + HEADER / 2 + (row - (plan.rows - 1) / 2) * (plan.cell[1] + GAP),
  ]
}

function footprint(size: Vec3, rotation: number): Vec3 {
  const cosine = Math.abs(Math.cos(rotation))
  const sine = Math.abs(Math.sin(rotation))
  return [size[0] * cosine + size[2] * sine, size[1], size[0] * sine + size[2] * cosine]
}

function finiteVector(value: unknown, length: number, positive = false): value is readonly number[] {
  return Array.isArray(value) && value.length === length
    && value.every((coordinate) => Number.isFinite(coordinate) && (!positive || coordinate > 0))
}

function validateScene(projection: Projection): void {
  const ids = new Set<string>()
  for (const component of [...projection.scene.zones, ...projection.scene.pieces]) {
    if (!component.id || component.id.startsWith(DECORATION) || component.id.startsWith('__env:')) throw new Error(`Reserved or empty table component ID: ${component.id}`)
    if (ids.has(component.id)) throw new Error(`Duplicate table component ID: ${component.id}`)
    ids.add(component.id)
  }
  const zones = new Set(projection.scene.zones.map((zone) => zone.id))
  for (const piece of projection.scene.pieces) {
    if (!zones.has(piece.zone)) throw new Error(`Unknown zone ${piece.zone} for piece ${piece.id}`)
    if (piece.count !== undefined && (!Number.isSafeInteger(piece.count) || piece.count < 0)) {
      throw new Error(`Invalid public count for piece ${piece.id}`)
    }
    if (piece.size !== undefined && !finiteVector(piece.size, 3, true)) {
      throw new Error(`Piece ${piece.id} needs a finite positive size`)
    }
    if (piece.position !== undefined && (!finiteVector(piece.position, 3) || piece.position[1] < 0)) {
      throw new Error(`Piece ${piece.id} needs a finite position on or above the playing surface`)
    }
    if (piece.rotation !== undefined && !Number.isFinite(piece.rotation)) {
      throw new Error(`Piece ${piece.id} needs a finite rotation`)
    }
  }
  for (const zone of projection.scene.zones) {
    if (zone.position !== undefined && (!finiteVector(zone.position, 3) || zone.position[1] < 0)) {
      throw new Error(`Zone ${zone.id} needs a finite position on or above the table`)
    }
    if (zone.size !== undefined && !finiteVector(zone.size, 2, true)) {
      throw new Error(`Zone ${zone.id} needs a finite positive size`)
    }
    if (zone.columns !== undefined && (!Number.isSafeInteger(zone.columns) || zone.columns < 1)) {
      throw new Error(`Zone ${zone.id} needs a positive integer column count`)
    }
  }
}
